import { createSlice } from '@reduxjs/toolkit';
import { globalApi } from '../services/globalApi';
import type { RootState } from './index';
import {
  getLocalSelectedOU,
  updateLocalSelectedOU
} from '../services/localTokenSrv';
export interface ITreeData {
  label: string;
  value: string;
  nodeId: number;
  desc: string;
  level: number;
  rootNode?: boolean;
  idPath: string[];
  namePath: string[];
  children?: ITreeData[];
  isLeaf?: boolean;
  data?: Record<string, any>;
  hasLine?: boolean;
}
interface Node {
  node_id: number;
  node_name: string;
  node_type: string;
  uid?: string; // only root node has uid for uniq value
  access_from?: Record<string, any>; // only root node has access_from
  parent_id: number;
  node_desc: string;
  id_path: string[];
  name_path: string[];
  ou_node_id?: number;
  ou_name?: string;
  ou_description?: string;
  account_node_id?: number;
  account_name?: string;
  account_email?: string;
  account_company?: string;
  account_description?: string;
  master_user_name?: string;
}
interface OrgNode {
  ou_node_id: number;
  parent_id: number;
  ou_name: string;
  ou_description?: string;
}
interface AccountNode {
  account_node_id: number;
  account_id: number;
  parent_id: number;
  account_name: string;
  account_email?: string;
  account_company?: string;
  account_description?: string;
  master_user_name?: string;
}
interface FcData {
  org_id: number;
  org_name: string;
  org_description: string;
  org_account_role: string;
  org_units: Array<OrgNode>;
  node_id: number;
  member_accounts: Array<AccountNode>;
  access_from: Record<string, any>;
  permission_scope: Record<string, any>;
  user_auth_passed: boolean;
  user_auth_url: string;
  user_display_info: Record<string, any>;
  portals: Record<string, any>[];
  user_menu: Record<string, any>[];
  support_menu: Record<string, any>[];
}
interface IProps {
  childrenByParent: Map<number, any[]>;
  parent: Node;
  level: number;
  namePath: string[];
  idPath: string[];
  orgId: string;
  accessFrom: Record<string, any>;
}

const buildChildrenByParent = (
  nodes: Node[],
  accessFrom: Record<string, any>,
  orgId: string | number
): Map<number, any[]> => {
  const childrenByParent = new Map<number, any[]>();
  const push = (node: any) => {
    const list = childrenByParent.get(node.parent_id);
    if (list) {
      list.push(node);
    } else {
      childrenByParent.set(node.parent_id, [node]);
    }
  };
  // accounts first
  for (const node of nodes) {
    if (node?.node_type === 'account') {
      push({ ...node, access_from: accessFrom, org_id: orgId });
    }
  }
  // then orgs
  for (const node of nodes) {
    if (node?.node_type !== 'account') {
      push(node);
    }
  }
  return childrenByParent;
};

const generateTreeData = ({
  childrenByParent,
  parent,
  level,
  namePath,
  idPath,
  orgId,
  accessFrom
}: IProps): ITreeData => {
  const rootData = {
    label: parent.node_name,
    desc: parent.node_desc,
    value: idPath.join('//'),
    nodeId: parent.node_id,
    level: level,
    idPath: idPath,
    namePath: namePath,
    rootNode: level === 1 ? true : false,
    data: parent
  };
  const children = childrenByParent.get(parent.node_id) || [];
  return {
    ...rootData,
    children: children.length
      ? children?.map((child) =>
          generateTreeData({
            childrenByParent: childrenByParent,
            parent: child,
            level: level + 1,
            namePath: [...rootData.namePath, child.node_name],
            idPath: [...rootData.idPath, `${child.node_id}`],
            orgId,
            accessFrom
          })
        )
      : undefined
  };
};

const initialState: Record<string, any> = {
  ou: [],
  data: [],
  accountOu: [],
  status: 'idle',
  error: {},
  selectedNode: {},
  nodesHasLine: {}
};
const fcOuSlice = createSlice({
  name: 'fcOU',
  initialState,
  reducers: {
    updateSelected: (state, { payload }) => {
      if (payload?.label) {
        state.selectedNode = payload;
        updateLocalSelectedOU(payload); //
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      globalApi.endpoints.getUserOrgNodes.matchFulfilled,
      (state, { payload }) => {
        const orgs: FcData[] = payload?.orgs;
        const accounts = payload?.accounts;
        if (orgs?.length) {
          const ouArr = orgs.map((org: Record<any, any>, index: number) => {
            const orgNodes =
              org.org_units.map((obj: Record<any, any>) => ({
                ...obj,
                node_type: 'org',
                node_id: obj?.ou_node_id,
                node_name: obj?.ou_name,
                node_desc: [obj?.ou_node_id, obj?.ou_description]
                  .filter((s) => s)
                  .join('/')
              })) || [];
            const permission = org.permission_scope;
            const memberAccounts =
              org?.member_accounts.map((obj: Record<string, any>) => ({
                ...obj,
                org_id: org?.org_id,
                access_from: org?.access_from,
                access_from_user_id: org?.access_from?.user_id
              })) || [];
            const allAccounts = [...memberAccounts];
            const accountNodes =
              allAccounts.map((obj) => {
                if (obj) {
                  return {
                    ...obj,
                    parent_id: orgNodes?.length
                      ? obj.parent_id
                      : org.node_id || org.org_id,
                    node_type: 'account',
                    node_id: generateAccountNodeId(obj),
                    node_name: generateAccountNodeName(obj),
                    node_desc: generateAccountDescription(obj)
                  };
                } else {
                  return {};
                }
              }) || [];
            const nodes: Node[] = [...orgNodes, ...accountNodes];
            const uid = [org.access_from?.user_id, org.node_id, org.org_id]
              .filter((n) => n)
              .join('-');
            const rootNode: Node = {
              node_type: 'org',
              node_name: org.org_name,
              node_id: org.node_id || org.org_id,
              uid: uid,
              id_path: [uid],
              name_path: [org.org_name],
              access_from: org.access_from,
              parent_id: -1,
              node_desc: [
                org.org_id,
                org.org_account_role,
                org.access_from?.user_id,
                org.org_description
              ]
                .filter((s) => s)
                .join('/')
            };
            const entryNode: Node | undefined = getPermissionEntryNode(
              permission?.node_id,
              nodes
            );
            const firstNode = entryNode?.node_id
              ? {
                  ...entryNode,
                  name_path: [entryNode?.node_name || 'unknown'],
                  id_path: [`${entryNode?.node_id}` || '-1']
                }
              : rootNode;
            const childrenByParent = buildChildrenByParent(
              nodes,
              org.access_from,
              org.org_id
            );
            const newOU = generateTreeData({
              childrenByParent: childrenByParent,
              parent: firstNode,
              level: 1,
              namePath: firstNode.name_path,
              idPath: firstNode.id_path,
              orgId: org.org_id,
              accessFrom: org.access_from
            });
            const maxLevel = getMaxLevel(newOU);
            const styledOU = isAllChildSingleLevel(newOU, state);
            return { ...styledOU, maxLevel: maxLevel };
          });
          const rootAccounts = accounts.filter(
            (account: Record<string, any>) =>
              !account?.permission_scope ||
              account?.permission_scope?.node_type === 'Account'
          );
          const ou = [
            ...ouArr,
            ...rootAccounts.map((obj: Record<any, any>) => {
              return obj
                ? {
                    ...obj,
                    label: generateAccountNodeName(obj),
                    desc: generateAccountDescription(obj),
                    value: generateAccountNodeId(obj),
                    nodeId: generateAccountNodeId(obj),
                    level: 1,
                    idPath: [generateAccountNodeId(obj)],
                    namePath: [generateAccountNodeName(obj)],
                    data: {
                      ...obj,
                      node_type: 'account',
                      root_account: true,
                      node_id: generateAccountNodeId(obj),
                      node_name: generateAccountNodeName(obj),
                      node_desc: generateAccountDescription(obj)
                    }
                  }
                : {};
            })
          ];
          state.ou = ou;
          const localSelectedOU = getLocalSelectedOU();
          const localNode = getSelectedTreeNode(localSelectedOU, ou);
          if (!state.selectedNode?.nodeId) {
            state.selectedNode = localNode || ou[0];
          }
          state.data = orgs;
          state.status = 'succeeded';
        } else if (accounts.length) {
          const rootNode: Node = {
            node_type: 'org',
            node_name: 'Accounts',
            node_id: 1,
            uid: '1',
            id_path: ['1'],
            name_path: ['Accounts'],
            access_from: {},
            parent_id: -1,
            node_desc: ''
          };
          const accountNodes =
            accounts.map((obj: Record<string, any>) => {
              if (obj) {
                return {
                  ...obj,
                  parent_id: 1,
                  node_type: 'account',
                  node_id: generateAccountNodeId(obj),
                  node_name: generateAccountNodeName(obj),
                  node_desc: generateAccountDescription(obj)
                };
              } else {
                return {};
              }
            }) || [];
          const childrenByParent = buildChildrenByParent(accountNodes, {}, '');
          const newOU = generateTreeData({
            childrenByParent: childrenByParent,
            parent: rootNode,
            level: 1,
            namePath: rootNode.name_path,
            idPath: rootNode.id_path,
            orgId: '',
            accessFrom: {}
          });
          const maxLevel = getMaxLevel(newOU);
          state.accountOu = [{ ...newOU, maxLevel: maxLevel }];
        }
        state.status = 'succeeded';
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getUserOrgNodes.matchPending,
      (state) => {
        state.status = 'loading';
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getUserOrgNodes.matchRejected,
      (state, { payload, error }) => {
        state.status = 'failed';
        state.error = error;
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getUserPermission.matchFulfilled,
      (state, { payload, meta }) => {
        const args = meta?.arg?.originalArgs as Record<string, any> | undefined;
        const nodeData = state.selectedNode?.data;
        if (!args || !nodeData) {
          return;
        }
        const sameAccountId =
          args.account_id == null ||
          String(args.account_id) === String(nodeData.account_id);
        const sameUserId =
          args.user_id == null ||
          String(args.user_id) === String(nodeData.user_id);
        const sameNodeId =
          args.account_node_id == null ||
          String(args.account_node_id) ===
            String(nodeData.account_node_id || nodeData.node_id);
        if (sameAccountId && sameUserId && sameNodeId) {
          state.selectedNode = {
            ...state.selectedNode,
            data: { ...nodeData, ...payload }
          };
        }
      }
    );
  }
});

export const generateAccountNodeId = (
  obj: Record<any, any>
): string | number => {
  const user_id = obj.user_id || '-1';
  const account_node_id = obj.account_node_id || '-1';
  const account_id = obj.account_id || '-1';
  return [account_node_id, account_id, user_id].filter((id) => id).join('-');
};

export const generateAccountNodeName = (obj: Record<any, any>): string =>
  obj?.account_name || obj?.account_company || 'Undefined';
export const generateAccountDescription = (obj: Record<any, any>): string => {
  const description = obj.account_description || obj.account_email;
  const role = obj.idp_role_name || '';
  return [description, role].filter((str) => str).join(' | ');
};

export const getAllOuNodes = (ouData: ITreeData[]): ITreeData[] => {
  if (!ouData?.length) {
    return [];
  }
  const result: ITreeData[] = [];
  const stack: ITreeData[] = [];
  for (let i = ouData.length - 1; i >= 0; i--) {
    stack.push(ouData[i]);
  }
  while (stack.length) {
    const node = stack.pop() as ITreeData;
    result.push(node);
    if (node.children?.length) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push(node.children[i]);
      }
    }
  }
  return result;
};

const findInTree = (
  ou: ITreeData[],
  predicate: (node: ITreeData) => boolean
): ITreeData | undefined => {
  if (!ou?.length) {
    return undefined;
  }
  const stack: ITreeData[] = [];
  for (let i = ou.length - 1; i >= 0; i--) {
    stack.push(ou[i]);
  }
  while (stack.length) {
    const node = stack.pop() as ITreeData;
    if (predicate(node)) {
      return node;
    }
    if (node.children?.length) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push(node.children[i]);
      }
    }
  }
  return undefined;
};

export const getSelectedTreeNode = (
  selectedNode: Record<string, any>,
  ou: ITreeData[]
): ITreeData | undefined => {
  const targetPath = selectedNode?.idPath?.join('//');
  if (!targetPath) {
    return undefined;
  }
  return findInTree(ou, (node) => node?.idPath?.join('//') === targetPath);
};
export const getSelectedTrees = (
  selectedNode: Record<string, any>,
  ou: ITreeData[]
): (ITreeData | ITreeData[])[] => {
  const findNode = getSelectedTreeNode(selectedNode, ou);
  return findNode ? [findNode] : ou;
};
export const getSelectedScopeTrees = (
  permissionScope: Record<string, any>,
  ou: ITreeData[]
): ITreeData | undefined => {
  const permission_scope = permissionScope || {};
  const scope =
    permission_scope?.node_type === 'Organization'
      ? permission_scope
      : undefined;

  if (!scope) {
    return undefined;
  }
  return findInTree(
    ou,
    (node) =>
      node.data?.org_id === scope?.entry_id &&
      node.data?.node_id === scope?.node_id
  );
};

export const findRedirectNode = (
  ou: ITreeData[],
  redirect_obj: Record<string, any>
) => {
  if (!ou) {
    return undefined;
  }
  const redirect_node_id = String(redirect_obj?.node_id || '');
  const redirect_access_user_id = String(
    redirect_obj?.access_from?.user_id || ''
  );
  const redirect_user_id = String(redirect_obj?.user_id || '');
  const redirect_account_id = String(redirect_obj?.account_id || '');

  return findInTree(ou, (node: Record<string, any>) => {
    const data = node.data || {};
    if (data?.node_type !== 'account' && !data?.account_id) {
      return false;
    }
    const data_node_id = String(data?.account_node_id || '');
    const data_user_id = String(data?.user_id || '');
    const data_access_user_id = String(data?.access_from?.user_id || '');
    const data_account_id = String(data?.account_id || '');

    // org member account must has 'node_id', 'access_from.user_id'
    if (redirect_node_id) {
      return (
        redirect_node_id === data_node_id &&
        redirect_access_user_id === data_access_user_id
      );
      // sub accounts only match user_id
    } else if (redirect_user_id && Number(redirect_user_id) > 0) {
      return redirect_user_id === data_user_id;
      // master account match account_id
    } else {
      return redirect_account_id === data_account_id;
    }
  });
};

const getPermissionEntryNode = (
  permissionNodeId: number,
  nodes: Node[]
): Node | undefined => {
  if (permissionNodeId) {
    return nodes?.find((node) => permissionNodeId === node.ou_node_id);
  }
  return undefined;
};
const getMaxLevel = (node: ITreeData): number => {
  let max = node?.level || 1;
  if (!node?.children?.length) {
    return max;
  }
  const stack: ITreeData[] = [...node.children];
  while (stack.length) {
    const current = stack.pop() as ITreeData;
    if ((current?.level || 1) > max) {
      max = current.level || 1;
    }
    if (current?.children?.length) {
      for (let i = current.children.length - 1; i >= 0; i--) {
        stack.push(current.children[i]);
      }
    }
  }
  return max;
};

const isAllChildSingleLevel = (node: ITreeData, state: any) => {
  if (
    node?.children &&
    node?.children?.every((child: ITreeData) => !child.children)
  ) {
    node.hasLine = true;
    state.nodesHasLine[node?.label + node?.level] = {
      label: node?.label,
      level: node?.level,
      children: node?.children.map((child: ITreeData) => ({
        label: child.label,
        level: child.level
      }))
    };
  } else if (node?.children) {
    const newChildren: ITreeData[] = node?.children?.map((child: ITreeData) => {
      return isAllChildSingleLevel(child, state);
    });
    node.children = newChildren;
  }
  return node;
};

const { reducer, actions } = fcOuSlice;
export const { updateSelected } = actions;

export const selectOU = (state: RootState) => state.fcOU.ou;
export const selectAccountOu = (state: RootState) => state.fcOU.accountOu;
export const selectOUStatus = (state: RootState) => state.fcOU.status;
export const selectOUError = (state: RootState) => state.fcOU.error;
export const getPreSelectedNode = (state: RootState) => state.fcOU.selectedNode;
export const selectLineNodes = (state: RootState) => state.fcOU.nodesHasLine;
export default reducer;
